
import React from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { translations } from '@/utils/translations';
import { Button } from '@/components/ui/button';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const WhyKIVideos = () => {
  const { currentLanguage } = useLanguage();
  const t = translations[currentLanguage];

  // Define SEO-optimized text content in case translations are missing
  const whyKIVideosTitle = t.whyKIVideosTitle || "KI Social Media Videos: 300% mehr Engagement & 70% Kostenersparnis";
  const whyKIVideosPoint1 = t.whyKIVideosPoint1 || "Expertenteam mit fortschrittlichen KI-Tools für Instagram, TikTok & Facebook Videos - stets auf dem neuesten Stand der Technologie";
  const whyKIVideosPoint2 = t.whyKIVideosPoint2 || "Team aus ausschließlich professionellen Video-Editoren mit langjähriger Erfahrung in digitaler Videoproduktion";
  const whyKIVideosPoint3 = t.whyKIVideosPoint3 || "Vollständige Fokussierung auf Ihren Erfolg durch maßgeschneiderte KI-gestützte Videostrategien";
  const whyKIVideosPoint4 = t.whyKIVideosPoint4 || "Professionelles Team mit Expertise in allen Social Media Formaten (Instagram Reels, Facebook Ads, TikTok)";

  // Contact page path based on current language
  const contactPath = `/${currentLanguage}/${
    currentLanguage === 'de' ? 'kontakt' : 
    currentLanguage === 'en' ? 'contact' : 
    currentLanguage === 'fr' ? 'contact' : 
    'contatto'
  }`;

  return (
    <section className="py-16 bg-[#030307]">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4 font-future">
            KI-Videomarketing für Social Media Plattformen
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Hochperformante ROI-optimierte Videos für Instagram, TikTok, Facebook & LinkedIn
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
          {/* Left side: Video replaced with marketing strategies text */}
          <div className="bg-black/40 rounded-lg overflow-hidden shadow-xl shadow-purple-900/10 p-6">
            <h3 className="text-2xl font-bold text-white mb-4">
              Video Marketing Strategien: Mehr Reichweite und Konversionen
            </h3>
            
            <div className="space-y-4 text-gray-200">
              <p>
                Effektives Videomarketing bietet zahlreiche kreative Möglichkeiten, um Ihre Zielgruppe zu erreichen und Konversionen zu steigern:
              </p>
              
              <ul className="space-y-3">
                <li className="flex items-start">
                  <span className="text-purple-300 font-bold mr-2">•</span>
                  <span><span className="font-semibold text-purple-200">Erklärvideo:</span> Kommunizieren Sie Ihr Wertversprechen präzise und verständlich</span>
                </li>
                <li className="flex items-start">
                  <span className="text-purple-300 font-bold mr-2">•</span>
                  <span><span className="font-semibold text-purple-200">Tutorials:</span> Bieten Sie Schritt-für-Schritt-Anleitungen zu Ihrem Produkt oder Service</span>
                </li>
                <li className="flex items-start">
                  <span className="text-purple-300 font-bold mr-2">•</span>
                  <span><span className="font-semibold text-purple-200">Kundentestimonials:</span> Schaffen Sie Vertrauen mit authentischen Bewertungen</span>
                </li>
                <li className="flex items-start">
                  <span className="text-purple-300 font-bold mr-2">•</span>
                  <span><span className="font-semibold text-purple-200">Hinter-den-Kulissen:</span> Zeigen Sie Ihre Unternehmenskultur und stärken Sie Beziehungen</span>
                </li>
                <li className="flex items-start">
                  <span className="text-purple-300 font-bold mr-2">•</span>
                  <span><span className="font-semibold text-purple-200">Kurz-Content:</span> Erstellen Sie aufmerksamkeitsstarke Clips für TikTok und Instagram</span>
                </li>
              </ul>
              
              <p className="pt-2 font-medium text-purple-300">
                SEO-Optimierung für Video-Content:
              </p>
              
              <ul className="space-y-2">
                <li className="flex items-start">
                  <span className="text-purple-300 font-bold mr-2">•</span>
                  <span>Keyword-Recherche für relevante Videothemen</span>
                </li>
                <li className="flex items-start">
                  <span className="text-purple-300 font-bold mr-2">•</span>
                  <span>Überzeugende Titel und Beschreibungen mit Keywords</span>
                </li>
                <li className="flex items-start">
                  <span className="text-purple-300 font-bold mr-2">•</span>
                  <span>Video-Transkripte für Suchmaschinen bereitstellen</span>
                </li>
              </ul>
            </div>
          </div>
          
          {/* Right side: Text content with enhanced visibility */}
          <div className="bg-black rounded-xl shadow-2xl p-8 border-2 border-purple-500">
            <h3 className="text-3xl font-bold text-white mb-8 font-future tracking-wide">
              {whyKIVideosTitle}
            </h3>
            
            <ul className="space-y-5">
              <li className="flex items-start bg-gray-900 p-4 rounded-lg border border-purple-500/50">
                <span className="text-purple-300 font-bold mr-4 mt-1 text-2xl">✓</span>
                <span className="text-white font-bold text-xl leading-tight">{whyKIVideosPoint1}</span>
              </li>
              <li className="flex items-start bg-gray-900 p-4 rounded-lg border border-purple-500/50">
                <span className="text-purple-300 font-bold mr-4 mt-1 text-2xl">✓</span>
                <span className="text-white font-bold text-xl leading-tight">{whyKIVideosPoint2}</span>
              </li>
              <li className="flex items-start bg-gray-900 p-4 rounded-lg border border-purple-500/50">
                <span className="text-purple-300 font-bold mr-4 mt-1 text-2xl">✓</span>
                <span className="text-white font-bold text-xl leading-tight">{whyKIVideosPoint3}</span>
              </li>
              <li className="flex items-start bg-gray-900 p-4 rounded-lg border border-purple-500/50">
                <span className="text-purple-300 font-bold mr-4 mt-1 text-2xl">✓</span>
                <span className="text-white font-bold text-xl leading-tight">{whyKIVideosPoint4}</span>
              </li>
            </ul>
            
            <div className="mt-10">
              <Link to={contactPath}>
                <Button className="bg-purple-600 hover:bg-purple-700 text-xl py-7 px-6 font-bold text-white">
                  Jetzt starten <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyKIVideos;
